import React from "react";
import { Crown, Sparkles } from "lucide-react";

/**
 * Founding Member + Day One badges.
 *
 * Two surfaces:
 *   - `<FoundingMemberBadge>` — purple Crown chip. Granted to every
 *     beta_allowlist member.
 *   - `<DayOneBadge>` — gold Sparkles chip. The first 100 signed-up
 *     users by `created_at` ASC.
 *
 * A user can have BOTH; render `<MemberBadges>` to display them
 * stacked side by side (Day One first because it's rarer).
 *
 * Variants:
 *   - default: pill chip for table rows / inline header use
 *   - large:   bigger, for profile / settings hero
 *   - mini:    icon-only, for tight spaces (share card composites)
 */

const SIZE = {
  default: {
    container: "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium",
    icon: "h-3.5 w-3.5",
  },
  large: {
    container: "inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm font-medium",
    icon: "h-4 w-4",
  },
  mini: {
    container: "inline-flex items-center justify-center rounded-full h-6 w-6",
    icon: "h-3.5 w-3.5",
  },
};

export const FoundingMemberBadge = ({
  variant = "default",
  className = "",
  showLabel = true,
}) => {
  const s = SIZE[variant] || SIZE.default;
  return (
    <span
      className={`${s.container} bg-gradient-to-br from-[#3a2e4a] to-[#5D4050] text-[#FFE5DD] border border-[#9B6A6C]/40 ${className}`}
      title="Founding Member — supported TruBeauté since the beta"
      data-testid="founding-member-badge"
    >
      <Crown className={s.icon} />
      {variant !== "mini" && showLabel && <span>Founding Member</span>}
    </span>
  );
};

export const DayOneBadge = ({
  variant = "default",
  className = "",
  showLabel = true,
}) => {
  const s = SIZE[variant] || SIZE.default;
  return (
    <span
      className={`${s.container} bg-gradient-to-br from-[#5D4530] to-[#8B6730] text-[#FFEFD5] border border-[#D4A14A]/50 ${className}`}
      title="Day One — among the first 100 to sign up"
      data-testid="day-one-badge"
    >
      <Sparkles className={s.icon} />
      {variant !== "mini" && showLabel && <span>Day One</span>}
    </span>
  );
};

/**
 * Helper: render whatever badges the user qualifies for, in a sensible
 * visual order (Day One first because rarer, then Founding Member).
 */
export const MemberBadges = ({
  isFoundingMember = false,
  isDayOne = false,
  variant = "default",
  className = "",
}) => {
  if (!isFoundingMember && !isDayOne) return null;
  return (
    <span className={`inline-flex items-center gap-2 flex-wrap ${className}`}>
      {isDayOne && <DayOneBadge variant={variant} />}
      {isFoundingMember && <FoundingMemberBadge variant={variant} />}
    </span>
  );
};

export default MemberBadges;
